// COPIADO de src/domain/rotation.ts — não edite aqui; edite no gestor
/**
 * rotation.ts — a ROTAÇÃO AUTOMÁTICA.
 *
 * Este arquivo é a implementação da decisão de produto mais importante do
 * SimpleIndor: **o usuário nunca cria playlist**. Ele publica anúncios; o
 * sistema monta a sequência. O receptor só recebe uma fila pronta e a executa.
 *
 * DECISÕES (cada uma com o porquê):
 *
 * 1. QUEM MONTA A FILA: a mesma função pura roda no backend (fonte de verdade,
 *    dentro do endpoint GET /screens/:id/queue) e no receptor (para revalidar a
 *    programação offline). Escrever isso duas vezes seria garantia de divergir.
 *    Como não há I/O aqui, os dois lados podem importar este módulo.
 *
 * 2. ORDEM: determinística. A base é `adId` em ordem lexicográfica crescente —
 *    estável entre chamadas, independente da ordem que o banco devolveu, o que
 *    faz a `version` (content hash) ficar estável quando nada mudou de verdade.
 *
 * 3. JUSTIÇA: rotação contínua por tempo. Em vez de "embaralhar", modelamos um
 *    LOOP GLOBAL de duração `soma das durações dos itens`. O instante `now`
 *    cai em algum ponto desse loop; o item que está "na vez" naquele instante
 *    vai para a frente da fila (rotação circular). Duas consequências boas:
 *      - anúncio curto aparece mais vezes que anúncio longo (justo por segundo
 *        de exibição, não por contagem de exibições);
 *      - telas diferentes recebem fases diferentes, então não piscam juntas.
 *    Sem `Math.random`: o mesmo `now` sempre produz a mesma fila (o que torna
 *    o comportamento auditável e os testes determinísticos).
 *
 * 4. DURAÇÃO: vídeo = duração real; se o backend ainda não analisou o arquivo,
 *    cai no fallback (`FALLBACK_VIDEO_SECONDS`) em vez de exibir meio segundo.
 *    Imagem = `defaultImageSeconds` (10s na v1). "Duração configurável por
 *    anúncio" está FORA do MVP — quando entrar, vira campo do `PlaybackItem`
 *    calculado aqui, sem mudar a assinatura do receptor.
 *
 * 5. ENTRADA/SAÍDA DA ROTAÇÃO: não existe endpoint de "adicionar à playlist".
 *    Um anúncio entra quando `status='active'` E está na janela de agendamento
 *    E está atribuído à tela. Sai quando é pausado, excluído, vence (`endsAt`)
 *    ou quando o usuário desmarca a tela. Tudo isso é refletido automaticamente
 *    no próximo GET/realtime — ver docs/architecture/ARCHITECTURE.md.
 */

import type { Ad, ID, PlaybackItem, PlaybackQueue } from './types';
import { isAdActiveAt } from './schedule';
import { computeQueueVersion } from './queueSignature';

/** Tempo padrão de exibição de imagem na v1 (segundos). */
export const DEFAULT_IMAGE_SECONDS = 10;

/** Fallback quando o vídeo não tem `durationSeconds` (arquivo em processamento). */
export const FALLBACK_VIDEO_SECONDS = 15;

/** Piso de sanidade: nenhuma mídia roda menos que isto. */
export const MIN_ITEM_SECONDS = 1;

export interface BuildQueueOptions {
  /** Tempo de exibição de imagem. Default: DEFAULT_IMAGE_SECONDS (10s). */
  defaultImageSeconds?: number;
  /** Fallback de duração de vídeo sem metadado. Default: FALLBACK_VIDEO_SECONDS. */
  fallbackVideoSeconds?: number;
  /**
   * Epoch (segundos) usado como origem do loop global de rotação.
   * Deixar fixo por padrão garante que backend e receptor calculem a MESMA fase
   * — se cada lado usasse "agora do processo", as fases divergiriam.
   */
  rotationEpochSeconds?: number;
}

/** 2026-01-01T00:00:00Z — origem arbitrária e imutável do loop de rotação. */
export const ROTATION_EPOCH_SECONDS = 1767225600;

/**
 * Anúncios que devem rodar nesta tela agora.
 * Filtro em uma passada, com os três critérios do produto, nesta ordem de custo:
 * atribuição à tela -> publicado -> dentro da janela de tempo.
 */
export function selectActiveAdsForScreen(screenId: ID, ads: Ad[], now: Date): Ad[] {
  return ads.filter(
    ad =>
      Boolean(ad) &&
      Array.isArray(ad.screenIds) &&
      ad.screenIds.indexOf(screenId) !== -1 &&
      ad.status === 'active' &&
      isAdActiveAt(ad, now),
  );
}

/** Duração resolvida de um anúncio, em segundos inteiros >= MIN_ITEM_SECONDS. */
export function resolveDurationSeconds(ad: Ad, opts?: BuildQueueOptions): number {
  const fallbackVideo = opts?.fallbackVideoSeconds ?? FALLBACK_VIDEO_SECONDS;
  const raw =
    ad.media?.kind === 'video'
      ? ad.media.durationSeconds && ad.media.durationSeconds > 0
        ? ad.media.durationSeconds
        : fallbackVideo
      : opts?.defaultImageSeconds ?? DEFAULT_IMAGE_SECONDS;
  const rounded = Math.round(raw);
  return rounded < MIN_ITEM_SECONDS ? MIN_ITEM_SECONDS : rounded;
}

/**
 * Deslocamento (índice do item que está "na vez") derivado do relógio.
 * Modelo: loop global de tamanho = soma das durações. `now` cai em uma posição
 * `t` desse loop; devolvemos o índice do item cujo intervalo contém `t`.
 */
function rotationOffsetIndex(items: PlaybackItem[], now: Date, opts?: BuildQueueOptions): number {
  if (items.length <= 1) {
    return 0;
  }
  const loopLength = items.reduce((sum, item) => sum + item.durationSeconds, 0);
  if (loopLength <= 0) {
    return 0;
  }
  const epoch = opts?.rotationEpochSeconds ?? ROTATION_EPOCH_SECONDS;
  const elapsed = Math.floor(now.getTime() / 1000) - epoch;
  // Módulo positivo (JS `%` devolve negativo para negativos — antes de 2026).
  const position = ((elapsed % loopLength) + loopLength) % loopLength;

  let cursor = 0;
  for (let i = 0; i < items.length; i += 1) {
    cursor += items[i].durationSeconds;
    if (position < cursor) {
      return i;
    }
  }
  return 0;
}

/** Rotação circular: [a,b,c,d] com offset 2 -> [c,d,a,b]. */
function rotate<T>(list: T[], offset: number): T[] {
  if (list.length <= 1 || offset <= 0) {
    return list.slice();
  }
  const normalized = offset % list.length;
  return list.slice(normalized).concat(list.slice(0, normalized));
}

/**
 * Monta a fila de reprodução de uma tela. Função pura: sem I/O, sem relógio
 * interno, sem dependência nova.
 *
 * Garantias contratuais (o receptor pode confiar):
 * - tela sem anúncios => `items: []` (o receptor mostra a tela de espera);
 * - só entram anúncios atribuídos a `screenId`, ativos e dentro da janela;
 * - `durationSeconds` sempre >= 1 e já resolvido (imagem/vídeo);
 * - `version` é o ETag DA TELA: depende do `screenId` e do conteúdo, mas não do
 *   instante (nem da ordem rotacionada), então duas telas com o mesmo conjunto
 *   de anúncios NÃO compartilham a mesma `version`.
 */
export function buildPlaybackQueue(
  screenId: ID,
  ads: Ad[],
  now: Date,
  opts?: BuildQueueOptions,
): PlaybackQueue {
  const active = selectActiveAdsForScreen(screenId, ads, now);

  // Ordem base estável: por adId. Nunca por data de criação — dois anúncios
  // publicados no mesmo segundo devem ter ordem reproduzível.
  const sorted = active
    .slice()
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const items: PlaybackItem[] = sorted.map(ad => ({
    adId: ad.id,
    media: ad.media,
    durationSeconds: resolveDurationSeconds(ad, opts),
    // Repasse do enquadramento escolhido no app (Bloco N). Spread CONDICIONAL: campo
    // ausente no anúncio continua ausente no item — `rotation: 0` é valor legítimo
    // (por isso a checagem é `!== undefined`, nunca "falsy"), e um `scaleMode:
    // undefined` explícito sujaria o JSON que vai para a TV.
    ...(ad.scaleMode !== undefined ? { scaleMode: ad.scaleMode } : {}),
    ...(ad.rotation !== undefined ? { rotation: ad.rotation } : {}),
  }));

  const rotated = rotate(items, rotationOffsetIndex(items, now, opts));

  return {
    screenId,
    // Hash do conteúdo canônico (screenId + itens ordenados por adId) => estável
    // e independente da fase, mas PRIVADO da tela (ETag por tela, ver queueSignature.ts).
    version: computeQueueVersion(rotated, screenId),
    items: rotated,
    generatedAt: now.toISOString(),
  };
}

/** Tamanho estimado do loop em segundos (usado pela UI/receptor para exibir "volta a cada X min"). */
export function queueLoopSeconds(queue: PlaybackQueue): number {
  return (queue.items ?? []).reduce((sum, item) => sum + item.durationSeconds, 0);
}
